/*
 * Emberline — cave-painting theme.
 * Everything is ochre, charcoal and soot on rough stone. Type is warm and
 * slightly irregular; edges are hand-drawn rather than geometric.
 */

@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600&family=Gentium+Book+Plus:ital,wght@0,400;0,700;1,400&display=swap');

:root {
  --stone-deep: #14100c;
  --stone: #1e1811;
  --stone-raised: #292018;
  --stone-edge: #3a2d21;

  --ochre: #c8712e;
  --ochre-bright: #e69a4b;
  --ochre-dim: #8a4d21;
  --rust: #9d3b23;
  --rust-bright: #c9553a;
  --charcoal: #0d0a07;
  --bone: #e8dcc4;
  --bone-dim: #a89b83;
  --ash: #6f6355;
  --moss: #6b7a45;
  --moss-bright: #93a55f;

  --ember-glow: #ff9a3c;

  --font-display: 'Cinzel', 'EB Garamond', Georgia, serif;
  --font-body: 'Gentium Book Plus', Georgia, 'Times New Roman', serif;

  --radius: 3px;
  --panel-shadow: 0 2px 0 rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(232, 220, 196, 0.04);
}

* {
  box-sizing: border-box;
}

html,
body,
#app {
  height: 100%;
}

body {
  margin: 0;
  background-color: var(--stone-deep);
  /* Rock grain: three offset gradients read as uneven mineral rather than noise. */
  background-image:
    radial-gradient(circle at 18% 12%, rgba(200, 113, 46, 0.07), transparent 45%),
    radial-gradient(circle at 82% 78%, rgba(157, 59, 35, 0.06), transparent 40%),
    repeating-linear-gradient(
      117deg,
      rgba(232, 220, 196, 0.014) 0px,
      rgba(232, 220, 196, 0.014) 1px,
      transparent 1px,
      transparent 5px
    );
  color: var(--bone);
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}

h1,
h2,
h3,
h4 {
  font-family: var(--font-display);
  font-weight: 600;
  letter-spacing: 0.06em;
  margin: 0;
  color: var(--bone);
}

button {
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  cursor: pointer;
}

button:disabled {
  cursor: not-allowed;
}

/* --- Layout -------------------------------------------------------------- */

.app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 16px 48px;
}

.app-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 18px;
  align-items: start;
}

@media (max-width: 940px) {
  .app-body {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* --- Panels -------------------------------------------------------------- */

.panel {
  background: linear-gradient(180deg, var(--stone-raised), var(--stone));
  border: 1px solid var(--stone-edge);
  border-radius: var(--radius);
  box-shadow: var(--panel-shadow);
  padding: 14px 16px 16px;
  margin-bottom: 16px;
}

.panel-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.95rem;
  text-transform: uppercase;
  color: var(--ochre);
  border-bottom: 1px solid var(--stone-edge);
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.panel-title .hint {
  font-family: var(--font-body);
  font-size: 0.8rem;
  letter-spacing: 0;
  text-transform: none;
  color: var(--ash);
}

.empty-note {
  color: var(--ash);
  font-style: italic;
  font-size: 0.9rem;
  margin: 4px 0 0;
}

/* --- Header -------------------------------------------------------------- */

.header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 0 14px;
}

.header-title {
  font-size: 1.7rem;
  letter-spacing: 0.16em;
  color: var(--ochre-bright);
  text-shadow: 0 0 24px rgba(255, 154, 60, 0.25);
}

.header-age {
  font-family: var(--font-body);
  font-size: 0.95rem;
  color: var(--bone-dim);
  margin-top: 2px;
}

.header-age em {
  color: var(--ochre);
  font-style: normal;
}

.header-stats {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
}

.stat {
  text-align: right;
}

.stat-label {
  display: block;
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ash);
}

.stat-value {
  font-family: var(--font-display);
  font-size: 1.1rem;
  color: var(--bone);
}

.stat-value.accent {
  color: var(--ochre-bright);
}

/* --- Progress bars ------------------------------------------------------- */

.bar {
  position: relative;
  height: 6px;
  background: var(--charcoal);
  border-radius: 999px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--ochre-dim), var(--ochre-bright));
  transition: width 220ms linear;
}

.bar-fill.moss {
  background: linear-gradient(90deg, #4a5730, var(--moss-bright));
}

.bar-fill.rust {
  background: linear-gradient(90deg, var(--rust), var(--rust-bright));
}

/* --- Resource bar -------------------------------------------------------- */

.resource-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(158px, 1fr));
  gap: 10px;
}

.resource {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  padding: 7px 9px;
  background: rgba(13, 10, 7, 0.45);
  border: 1px solid var(--stone-edge);
  border-radius: var(--radius);
}

.resource.full {
  border-color: var(--ochre-dim);
}

.resource-name {
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ash);
}

.resource-amount {
  font-family: var(--font-display);
  font-size: 1rem;
  color: var(--bone);
}

.resource-cap {
  color: var(--ash);
  font-size: 0.8rem;
}

.resource-rate {
  font-size: 0.78rem;
  color: var(--moss-bright);
}

.resource-rate.negative {
  color: var(--rust-bright);
}

/* --- Tabs ---------------------------------------------------------------- */

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 14px;
}

.tab {
  background: transparent;
  border: 1px solid var(--stone-edge);
  border-radius: var(--radius);
  padding: 7px 14px;
  font-family: var(--font-display);
  font-size: 0.82rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--bone-dim);
  transition: color 120ms, border-color 120ms, background 120ms;
}

.tab:hover:not(:disabled) {
  color: var(--bone);
  border-color: var(--ochre-dim);
}

.tab.active {
  color: var(--charcoal);
  background: linear-gradient(180deg, var(--ochre-bright), var(--ochre));
  border-color: var(--ochre-bright);
}

.tab .badge {
  display: inline-block;
  margin-left: 7px;
  min-width: 17px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--rust);
  color: var(--bone);
  font-family: var(--font-body);
  font-size: 0.7rem;
  line-height: 17px;
  text-align: center;
}

.tab.active .badge {
  background: var(--charcoal);
  color: var(--ochre-bright);
}

/* --- Cards (jobs, buildings, techs, nodes) ------------------------------- */

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(268px, 1fr));
  gap: 10px;
}

.card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 11px 12px;
  background: rgba(13, 10, 7, 0.4);
  border: 1px solid var(--stone-edge);
  border-radius: var(--radius);
}

.card.locked {
  opacity: 0.45;
}

.card.owned {
  border-color: var(--moss);
  background: rgba(107, 122, 69, 0.09);
}

.card.affordable {
  border-color: var(--ochre-dim);
}

.card-head {
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr) auto;
  gap: 9px;
  align-items: center;
}

.card-name {
  font-family: var(--font-display);
  font-size: 0.95rem;
  color: var(--bone);
}

.card-count {
  font-family: var(--font-display);
  font-size: 1rem;
  color: var(--ochre-bright);
}

.card-desc {
  font-size: 0.85rem;
  color: var(--bone-dim);
  margin: 0;
}

.card-flavor {
  font-size: 0.82rem;
  font-style: italic;
  color: var(--ash);
  margin: 0;
}

.card-note {
  font-size: 0.8rem;
  color: var(--rust-bright);
  margin: 0;
}

.card-effects {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.effect-chip {
  font-size: 0.76rem;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(107, 122, 69, 0.16);
  border: 1px solid rgba(147, 165, 95, 0.3);
  color: var(--moss-bright);
}

.effect-chip.cost {
  background: rgba(157, 59, 35, 0.14);
  border-color: rgba(201, 85, 58, 0.3);
  color: #dd8f72;
}

.effect-chip.cost.short {
  opacity: 0.55;
  text-decoration: line-through;
}

.card-actions {
  display: flex;
  gap: 6px;
  margin-top: auto;
}

/* --- Buttons ------------------------------------------------------------- */

.btn {
  flex: 1;
  padding: 7px 10px;
  background: linear-gradient(180deg, var(--stone-raised), var(--stone));
  border: 1px solid var(--stone-edge);
  border-radius: var(--radius);
  font-family: var(--font-display);
  font-size: 0.82rem;
  letter-spacing: 0.06em;
  color: var(--bone-dim);
  transition: color 120ms, border-color 120ms, transform 60ms;
}

.btn:hover:not(:disabled) {
  color: var(--bone);
  border-color: var(--ochre-dim);
}

.btn:active:not(:disabled) {
  transform: translateY(1px);
}

.btn:disabled {
  opacity: 0.4;
}

.btn.primary {
  background: linear-gradient(180deg, var(--ochre), var(--ochre-dim));
  border-color: var(--ochre-bright);
  color: var(--charcoal);
}

.btn.primary:hover:not(:disabled) {
  color: var(--charcoal);
  filter: brightness(1.1);
}

.btn.danger {
  border-color: var(--rust);
  color: var(--rust-bright);
}

.btn.ghost {
  flex: 0 0 auto;
  min-width: 38px;
}

/* --- Jobs ---------------------------------------------------------------- */

.job-row {
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 9px 10px;
  border-bottom: 1px solid rgba(58, 45, 33, 0.6);
}

.job-row:last-child {
  border-bottom: none;
}

.job-row.locked {
  opacity: 0.42;
}

.job-name {
  font-family: var(--font-display);
  font-size: 0.92rem;
}

.job-rates {
  font-size: 0.8rem;
  color: var(--ash);
}

.job-rates .out {
  color: var(--moss-bright);
}

.job-rates .in {
  color: #dd8f72;
}

.job-controls {
  display: flex;
  align-items: center;
  gap: 5px;
}

.job-count {
  min-width: 44px;
  text-align: center;
  font-family: var(--font-display);
  font-size: 1.05rem;
  color: var(--ochre-bright);
}

.job-starved {
  color: var(--rust-bright);
  font-size: 0.76rem;
}

.idle-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  margin-bottom: 10px;
  background: rgba(200, 113, 46, 0.09);
  border: 1px solid var(--ochre-dim);
  border-radius: var(--radius);
  font-size: 0.88rem;
}

/* --- Ancestral tree ------------------------------------------------------ */

.ancestral-tier {
  margin-bottom: 16px;
}

.ancestral-tier-label {
  font-family: var(--font-display);
  font-size: 0.78rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ash);
  margin-bottom: 8px;
}

.node-level {
  font-size: 0.8rem;
  color: var(--ochre);
}

/* --- Tales --------------------------------------------------------------- */

.tale {
  padding: 9px 0;
  border-bottom: 1px solid rgba(58, 45, 33, 0.55);
}

.tale:last-child {
  border-bottom: none;
}

.tale-title {
  font-family: var(--font-display);
  font-size: 0.88rem;
  color: var(--bone);
}

.tale-title.favourable {
  color: var(--moss-bright);
}

.tale-title.grim {
  color: var(--rust-bright);
}

.tale-text {
  font-size: 0.84rem;
  color: var(--bone-dim);
  margin: 2px 0 0;
}

.tale-meta {
  font-size: 0.76rem;
  color: var(--ash);
  margin-top: 3px;
}

/* --- Toasts -------------------------------------------------------------- */

.toast-stack {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 40;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 320px;
}

.toast {
  padding: 10px 12px;
  background: linear-gradient(180deg, var(--stone-raised), var(--stone));
  border: 1px solid var(--ochre-dim);
  border-left-width: 3px;
  border-radius: var(--radius);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.55);
  animation: toast-in 220ms ease-out;
}

.toast.grim {
  border-color: var(--rust);
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateX(14px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* --- Modal --------------------------------------------------------------- */

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(8, 6, 4, 0.82);
}

.modal {
  width: 100%;
  max-width: 520px;
  max-height: 86vh;
  overflow-y: auto;
  padding: 22px 24px 20px;
  background: linear-gradient(180deg, var(--stone-raised), var(--stone-deep));
  border: 1px solid var(--ochre-dim);
  border-radius: var(--radius);
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.7);
}

.modal h2 {
  font-size: 1.3rem;
  letter-spacing: 0.12em;
  color: var(--ochre-bright);
  margin-bottom: 6px;
}

.modal p {
  color: var(--bone-dim);
  font-size: 0.92rem;
}

.modal-actions {
  display: flex;
  gap: 8px;
  margin-top: 18px;
}

.gain-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 6px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}

.gain-list li {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.88rem;
  padding: 4px 8px;
  background: rgba(13, 10, 7, 0.5);
  border-radius: var(--radius);
}

.gain-list .amount {
  color: var(--moss-bright);
}

/* --- Prestige ------------------------------------------------------------ */

.prestige-quote {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: baseline;
  margin-bottom: 12px;
}

.prestige-figure {
  font-family: var(--font-display);
  font-size: 2rem;
  color: var(--ochre-bright);
  text-shadow: 0 0 20px rgba(255, 154, 60, 0.3);
}

.age-track {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 10px 0 4px;
}

.age-pip {
  flex: 1 1 90px;
  padding: 6px 8px;
  border: 1px solid var(--stone-edge);
  border-radius: var(--radius);
  font-size: 0.76rem;
  color: var(--ash);
  text-align: center;
}

.age-pip.done {
  border-color: var(--moss);
  color: var(--moss-bright);
}

.age-pip.current {
  border-color: var(--ochre-bright);
  color: var(--ochre-bright);
  background: rgba(200, 113, 46, 0.1);
}

/* --- Settings ------------------------------------------------------------ */

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid rgba(58, 45, 33, 0.55);
}

.setting-row:last-of-type {
  border-bottom: none;
}

.setting-label {
  font-size: 0.9rem;
}

.setting-desc {
  font-size: 0.8rem;
  color: var(--ash);
}

.save-box {
  width: 100%;
  min-height: 92px;
  margin-top: 10px;
  padding: 8px 10px;
  background: var(--charcoal);
  border: 1px solid var(--stone-edge);
  border-radius: var(--radius);
  color: var(--bone-dim);
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 0.76rem;
  resize: vertical;
}

.footer-note {
  margin-top: 22px;
  text-align: center;
  font-size: 0.8rem;
  color: var(--ash);
}

/* --- Glyphs -------------------------------------------------------------- */

.glyph {
  display: block;
  flex: none;
  fill: none;
  stroke: var(--ochre);
  stroke-width: 6;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.glyph.bone-stroke {
  stroke: var(--bone-dim);
}

.glyph.moss-stroke {
  stroke: var(--moss-bright);
}
